<nav compact tuiSubheader [style.position]="'sticky'" class="bg-white">
  <tui-breadcrumbs [itemsLimit]="10">
    <ng-container *ngFor="let item of breadcrumbs; let last = last">
      <ng-container *ngIf="last; else notLast">
        <strong *tuiItem tuiFade>
          {{ item.label }}
        </strong>
      </ng-container>
      <ng-template #notLast>
        <button *tuiItem tuiLink type="button" (click)="navigate(item.link)" [disabled]="!item.link">
          {{ item.label }}
        </button>
      </ng-template>
    </ng-container>
  </tui-breadcrumbs>
</nav>

<tui-loader [overlay]="true" [showLoader]="isLoading && !newClient" class="w-full block min-h-[400px]">

  <section class="w-full">
    <p class="text-4xl font-normal mt-[0.9rem]">
      {{ newClient ? "Add New Client" : "Edit Client" }}
    </p>
    <p *ngIf="conversionBannerSummary"
      class="text-sm text-[#526ED3] bg-[#F9FBFF] border border-[#E5E7EA] rounded-md px-4 py-3 mt-3">
      Creating a new client from archived record
      <strong>{{ conversionBannerSummary }}</strong>. Review type-specific fields and
      assign a new ID before saving.
    </p>
    <form [formGroup]="clientForm" (ngSubmit)="saveUser()">
      <!-- Client Type — only when creating; type is fixed after the client exists -->
      <ng-container *ngIf="newClient">
        <div class="flex mt-5">
          <div>
            <p class="text-base font-medium text-[#3E4A58] my-0 w-[155px]">
              Client Type
            </p>
            <p class="text-sm font-normal text-[#949494D9] my-0 w-[155px]">
              Select the client type
            </p>
          </div>
          <div class="pl-[167px] w-full xl:w-1/3 2xl:w-1/3">
            <tui-combo-box formControlName="clientType" [stringify]="stringifyClientType"
              [tuiTextfieldLabelOutside]="true" placeholder="Select Client Type">
              <tui-data-list-wrapper *tuiDataList [items]="clientTypeOptions | tuiFilterByInput"
                [itemContent]="stringifyClientType | tuiStringifyContent" />
              Client Type
            </tui-combo-box>
            <tui-error formControlName="clientType" class="mb-3" [error]="[] | tuiFieldError | async" />
          </div>
        </div>
      </ng-container>

      <!-- =================================================================== -->
      <!-- REGISTERED CLIENT FORM -->
      <!-- =================================================================== -->
      <ng-container *ngIf="clientForm.get('clientType')?.value === CLIENT_TYPES.REGISTERED">
        <div class="flex mt-5">
          <div>
            <p class="text-base font-medium text-[#3E4A58] my-0 w-[155px]">
              Client Information
            </p>
            <p class="text-sm font-normal text-[#949494D9] my-0 w-[155px]">
              Enter client information
            </p>
          </div>
          <div class="pl-[167px] w-full xl:w-1/3 2xl:w-1/3">
            <label tuiLabel>
              <tui-input formControlName="registrationNo">
                Registration Number
                <input tuiTextfieldLegacy type="text" placeholder="0001 or REG-0001" maxlength="8" />
              </tui-input>
              <p *ngIf="suggestedClientId" class="text-xs text-[#191C30E5] ml-4 mt-1 mb-3 leading-4">
                Last Registered ID: <b>{{ lastClientId || "—" }}</b>
                <span class="mx-1">•</span>
                Suggested: <b>{{ suggestedClientId }}</b>
                <button *ngIf="newClient" type="button"
                  class="text-xs text-[#526ED3] underline decoration-dotted underline-offset-4 cursor-pointer bg-transparent border-none p-0 ml-2"
                  (click)="useSuggestedId()">
                  Use suggested
                </button>
              </p>
              <p class="text-xs text-[#949494] ml-4 mt-1 mb-2">
                Custom number: digits only (1–4). Full form: REG- plus exactly 4 digits.
              </p>
              <tui-error formControlName="registrationNo" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input-date formControlName="registrationDate">
                Registration Date
                <input tuiTextfieldLegacy />
              </tui-input-date>
              <app-recommended-hint [control]="clientForm.get('registrationDate')"
                message="Recommended. You can save without it and update later." />
              <tui-error formControlName="registrationDate" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input formControlName="guardianName">
                Client Name
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <tui-error formControlName="guardianName" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input formControlName="email">
                Client Email
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <app-recommended-hint [control]="clientForm.get('email')"
                message="Recommended. You can save without it and update later." />
              <button *ngIf="!newClient && !emailEditUnlocked" type="button"
                class="text-xs text-[#526ED3] underline decoration-dotted underline-offset-4 cursor-pointer bg-transparent border-none p-0 mt-1"
                (click)="unlockEmailEdit()" [appDisableUnlessPermission]="'CLIENT_UPDATE'">
                Edit email
              </button>
              <tui-error formControlName="email" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input formControlName="guardianContactNo">
                Client Contact Number (Whatsapp)
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <tui-error class="mb-3" formControlName="guardianContactNo" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input formControlName="guardianNIC">
                NIC Number
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <app-recommended-hint [control]="clientForm.get('guardianNIC')"
                message="Recommended. You can save without it and update later." />
              <tui-error formControlName="guardianNIC" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <div class="w-full">
              <label tuiLabel>
                <tui-textarea formControlName="guardianAddress" [tuiTextfieldLabelOutside]="true"
                  placeholder="Enter Address" title="Textarea Input">Address</tui-textarea>
                <app-recommended-hint [control]="clientForm.get('guardianAddress')"
                  message="Recommended. You can save without it and update later." />
                <tui-error formControlName="guardianAddress" class="mb-3" [error]="[] | tuiFieldError | async" />
              </label>
            </div>

          </div>
        </div>

        <div class="flex">
          <div>
            <p class="text-base font-medium text-[#3E4A58] my-0">
              Patient Information
            </p>
            <p class="text-sm font-normal text-[#949494D9] my-0">
              Enter patient information
            </p>
          </div>
          <div class="pl-[160px] w-full xl:w-1/3 2xl:w-1/3">
            <label tuiLabel>
              <tui-input formControlName="name" class="">
                Patient Name
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <tui-error formControlName="name" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input-date formControlName="dateOfBirth">
                Patient Date of birth
                <input tuiTextfieldLegacy />
              </tui-input-date>
              <app-recommended-hint [control]="clientForm.get('dateOfBirth')"
                message="Recommended. You can save without it and update later." />
              <tui-error formControlName="dateOfBirth" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input formControlName="age">
                Patient Age (Years)
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <app-recommended-hint [control]="clientForm.get('age')"
                message="Recommended. You can save without it and update later." />
              <tui-error formControlName="age" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input formControlName="ageMonths">
                Patient Age (Months)
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <app-recommended-hint [control]="clientForm.get('ageMonths')"
                message="Recommended. You can save without it and update later." />
              <tui-error formControlName="ageMonths" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>
          </div>
        </div>

        <div class="flex">
          <div>
            <p class="text-base font-medium text-[#3E4A58] my-0">
              Current Condition
            </p>
            <p class="text-sm font-normal text-[#949494D9] my-0">
              Enter the state of the client
            </p>
          </div>
          <div class="pl-[145px]">
            <div class="md:w-[350px] lg:w-[610px] xl:w-[420px] 2xl:w-[600px]">
              <label tuiLabel>
                <tui-textarea formControlName="currentCondition" [tuiTextfieldLabelOutside]="true"
                  placeholder="Enter Current Condition" title="Textarea Input">Start Typing...</tui-textarea>
                <app-recommended-hint [control]="clientForm.get('currentCondition')"
                  message="Recommended. You can save without it and update later." />
                <tui-error formControlName="currentCondition" [error]="[] | tuiFieldError | async" />
              </label>
            </div>
          </div>
        </div>

        <div class="flex">
          <div class="mt-5">
            <p class="text-base font-medium text-[#3E4A58] my-0">
              Other Information
            </p>
            <p class="text-sm font-normal text-[#949494D9] my-0">
              Enter other information
            </p>
          </div>
          <div class="pl-[171px] w-full xl:w-1/3 2xl:w-1/3 mt-5">

            <tui-combo-box formControlName="registrationType" [tuiTextfieldLabelOutside]="true"
              placeholder="Select Registration Type">
              <tui-data-list-wrapper *tuiDataList [items]="registrationOptions | tuiFilterByInput" />
              Registration Type
            </tui-combo-box>
            <tui-error formControlName="registrationType" class="mb-3" [error]="[] | tuiFieldError | async" />

            <label tuiLabel *ngIf="clientForm.get('registrationType')?.value">
              <tui-input formControlName="registrationFee" type="number" [tuiTextfieldLabelOutside]="true">
                Registration Fee (LKR)
                <input tuiTextfieldLegacy type="number" [readonly]="!feeEditUnlocked" />
              </tui-input>
              <app-recommended-hint *ngIf="newClient" [control]="clientForm.get('registrationFee')"
                message="Recommended. You can save without it and update later." />
              <div class="flex items-center gap-3 mt-1">
                <button type="button"
                  class="text-xs text-[#526ED3] underline decoration-dotted underline-offset-4 cursor-pointer bg-transparent border-none p-0"
                  (click)="toggleFeeEdit()" [appDisableUnlessPermission]="'CLIENT_UPDATE'">
                  {{ feeEditUnlocked ? 'Reset to system fee' : 'Override fee' }}
                </button>
              </div>
              <tui-error formControlName="registrationFee" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <div *ngIf="clientForm.get('registrationType')?.value === 'One Year'">
              <label tuiLabel>
                <tui-input-date formControlName="expiryDate">
                  Expiry Date
                  <input tuiTextfieldLegacy />
                </tui-input-date>
                <tui-error formControlName="expiryDate" [error]="[] | tuiFieldError | async" />
              </label>
            </div>


          </div>
        </div>


      </ng-container>

      <!-- =================================================================== -->
      <!-- UNREGISTERED CLIENT FORM (Non Reg Client) -->
      <!-- =================================================================== -->
      <ng-container *ngIf="clientForm.get('clientType')?.value === CLIENT_TYPES.UNREGISTERED">
        <div class="flex mt-5">
          <div>
            <p class="text-base font-medium text-[#3E4A58] my-0 w-[155px]">
              Client Information
            </p>
            <p class="text-sm font-normal text-[#949494D9] my-0 w-[155px]">
              Enter client information
            </p>
          </div>
          <div class="pl-[167px] w-full xl:w-1/3 2xl:w-1/3">
            <label tuiLabel>
              <tui-input formControlName="nonRegNo">
                Non Reg Client Number
                <input tuiTextfieldLegacy type="text" placeholder="0001 or NREG-0001" maxlength="9" />
              </tui-input>
              <p *ngIf="suggestedClientId" class="text-xs text-[#191C30E5] ml-4 mt-1 mb-3 leading-4">
                Last Non Registered ID: <b>{{ lastClientId || "—" }}</b>
                <span class="mx-1">•</span>
                Suggested: <b>{{ suggestedClientId }}</b>
                <button *ngIf="newClient" type="button"
                  class="ml-2 text-xs text-[#526ED3] underline decoration-dotted underline-offset-4 cursor-pointer bg-transparent border-none p-0"
                  (click)="useSuggestedId()">
                  Use suggested
                </button>
              </p>
              <p class="text-xs text-[#949494] ml-4 mt-1 mb-2">
                Custom number: digits only (1–4). Full form: NREG- plus exactly 4 digits.
              </p>
              <tui-error formControlName="nonRegNo" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input-date formControlName="startingDate">
                Starting Date
                <input tuiTextfieldLegacy />
              </tui-input-date>
              <tui-error formControlName="startingDate" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input formControlName="guardianName">
                Client Name
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <tui-error formControlName="guardianName" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input formControlName="email">
                Client Email
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <button *ngIf="!newClient && !emailEditUnlocked" type="button"
                class="text-xs text-[#526ED3] underline decoration-dotted underline-offset-4 cursor-pointer bg-transparent border-none p-0 mt-1"
                (click)="unlockEmailEdit()" [appDisableUnlessPermission]="'CLIENT_UPDATE'">
                Edit email
              </button>
              <tui-error formControlName="email" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input formControlName="guardianContactNo">
                Client Contact Number (Whatsapp)
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <tui-error class="mb-3" formControlName="guardianContactNo" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input formControlName="guardianNIC">
                NIC Number
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <tui-error formControlName="guardianNIC" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>


            <div class="w-full">
              <label tuiLabel>
                <tui-textarea formControlName="guardianAddress" [tuiTextfieldLabelOutside]="true"
                  placeholder="Enter Address" title="Textarea Input">Address</tui-textarea>
                <app-recommended-hint [control]="clientForm.get('guardianAddress')"
                  message="Recommended. You can save without it and update later." />
                <tui-error formControlName="guardianAddress" class="mb-3" [error]="[] | tuiFieldError | async" />
              </label>
            </div>


          </div>
        </div>

        <div class="flex">
          <div class="mt-5">
            <p class="text-base font-medium text-[#3E4A58] my-0 w-[155px]">
              Patient Information
            </p>
            <p class="text-sm font-normal text-[#949494D9] my-0 w-[170px]">
              Enter patient information
            </p>
          </div>
          <div class="pl-[150px] w-full xl:w-1/3 2xl:w-1/3 mt-5">
            <label tuiLabel>
              <tui-input formControlName="name">
                Patient Name
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <tui-error formControlName="name" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input-date formControlName="dateOfBirth">
                Patient Date of birth
                <input tuiTextfieldLegacy />
              </tui-input-date>
              <tui-error formControlName="dateOfBirth" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input formControlName="age">
                Patient Age (Years)
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <tui-error formControlName="age" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input formControlName="ageMonths">
                Patient Age (Months)
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <tui-error formControlName="ageMonths" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>
          </div>
        </div>

        <div class="flex">
          <div class="mt-5">
            <p class="text-base  font-medium text-[#3E4A58] my-0">
              Current Condition
            </p>
            <p class="text-sm font-normal text-[#949494D9] my-0">
              Enter the state of the patient
            </p>
          </div>
          <div class="pl-[133px]">
            <div class="md:w-[350px] lg:w-[610px] xl:w-[420px] 2xl:w-[600px]">
              <label tuiLabel class="mt-5">
                <tui-textarea formControlName="currentCondition" [tuiTextfieldLabelOutside]="true"
                  placeholder="Enter Current Condition" title="Textarea Input">Start Typing...</tui-textarea>
                <tui-error formControlName="currentCondition" [error]="[] | tuiFieldError | async" />
              </label>
            </div>
          </div>
        </div>


        <div class="flex">
          <div class="mt-5">
            <p class="text-base font-medium text-[#3E4A58] my-0 w-[155px]">
              Nursing Type
            </p>
            <p class="text-sm font-normal text-[#949494D9] my-0 w-[170px]">
              Select nursing type
            </p>
          </div>
          <div class="pl-[150px] w-full xl:w-1/3 2xl:w-1/3 mt-5">
            <tui-combo-box formControlName="nursingType" [tuiTextfieldLabelOutside]="true"
              placeholder="Select Nursing Type">
              <tui-data-list-wrapper *tuiDataList [items]="nursingTypeOptions | tuiFilterByInput" />
              Home Nursing
            </tui-combo-box>
            <tui-error formControlName="nursingType" class="mb-3" [error]="[] | tuiFieldError | async" />
          </div>
        </div>
      </ng-container>

      <!-- =================================================================== -->
      <!-- INWARD CLIENT FORM -->
      <!-- =================================================================== -->
      <ng-container *ngIf="clientForm.get('clientType')?.value === CLIENT_TYPES.INWARD">

        <div class="flex mt-5">
          <div>
            <p class="text-base font-medium text-[#3E4A58] my-0 w-[155px]">
              Client Information
            </p>
            <p class="text-sm font-normal text-[#949494D9] my-0 w-[155px]">
              Enter inward client details
            </p>
          </div>
          <div class="pl-[167px] w-full xl:w-1/3 2xl:w-1/3">
            <label tuiLabel>
              <tui-input formControlName="bhtNumber">
                BHT Number
                <input tuiTextfieldLegacy type="text" placeholder="00001 or BHT-00001" maxlength="10" />
              </tui-input>
              <p *ngIf="suggestedClientId" class="text-xs text-[#191C30E5] ml-4 mt-1 mb-3 leading-4">
                Last Inward BHT: <b>{{ lastClientId || "—" }}</b>
                <span class="mx-1">•</span>
                Suggested: <b>{{ suggestedClientId }}</b>
                <button *ngIf="newClient" type="button"
                  class="ml-2 text-xs text-[#526ED3] underline decoration-dotted underline-offset-4 cursor-pointer bg-transparent border-none p-0"
                  (click)="useSuggestedId()">
                  Use suggested
                </button>
              </p>
              <p class="text-xs text-[#949494] ml-4 mt-1 mb-2">
                Custom number: digits only (1–5). Full form: BHT- plus exactly 5 digits.
              </p>
              <tui-error formControlName="bhtNumber" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input formControlName="guardianName">
                Client Name
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <tui-error formControlName="guardianName" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input formControlName="guardianContactNo">
                Client Contact Number
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <tui-error class="mb-3" formControlName="guardianContactNo" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input-date formControlName="inwardDate">
                Date
                <input tuiTextfieldLegacy />
              </tui-input-date>
              <tui-error formControlName="inwardDate" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

          </div>
        </div>
        <div class="flex mt-5">
          <div>
            <p class="text-base font-medium text-[#3E4A58] my-0 w-[155px]">
              Patient Information
            </p>
            <p class="text-sm font-normal text-[#949494D9] my-0 w-[155px]">
              Enter patient details
            </p>
          </div>
          <div class="pl-[167px] w-full xl:w-1/3 2xl:w-1/3">
            <label tuiLabel>
              <tui-input formControlName="name">
                Patient Name
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <tui-error formControlName="name" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <label tuiLabel>
              <tui-input formControlName="roomNumber">
                Room Number
                <input tuiTextfieldLegacy type="text" placeholder="e.g. 304" />
              </tui-input>
              <tui-error formControlName="roomNumber" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>

            <tui-combo-box formControlName="meal" [tuiTextfieldLabelOutside]="true" placeholder="Select Meal Option">
              <tui-data-list-wrapper *tuiDataList [items]="mealOptions | tuiFilterByInput" />
              Meal
            </tui-combo-box>
            <tui-error formControlName="meal" class="mb-3" [error]="[] | tuiFieldError | async" />


            <tui-combo-box formControlName="roster" [tuiTextfieldLabelOutside]="true" placeholder="Select Roster">
              <tui-data-list-wrapper *tuiDataList [items]="rosterOptions | tuiFilterByInput" />
              Roster
            </tui-combo-box>
            <tui-error formControlName="roster" class="mb-3" [error]="[] | tuiFieldError | async" />
          </div>
        </div>
      </ng-container>

      <!-- =================================================================== -->
      <!-- COMMON: Current Location + Action buttons -->
      <!-- =================================================================== -->
      <div class="flex">
        <div class="mt-5">
          <p class="text-base font-medium text-[#3E4A58] my-0">
            Current Location
          </p>
          <p class="text-sm font-normal text-[#949494D9] my-0">(Optional)</p>
        </div>
        <div class="pl-[195px]">
          <div class="md:w-[350px] lg:w-[610px] xl:w-[420px] 2xl:w-[600px]">
            <label tuiLabel class="mt-5">
              <tui-textarea formControlName="currentLocation" [tuiTextfieldLabelOutside]="true"
                placeholder="Enter Current Location" title="Textarea Input">Start Typing...</tui-textarea>
            </label>
          </div>
          <!-- <div class="mt-5 space-x-5 flex justify-end">
          <button
            [routerLink]="['/admin/client']"
            appearance="outline"
            tuiButton
            type="button"
            class="bg-black"
          >
            Cancel
          </button>
          <button
            appearance="primary"
            tuiButton
            type="submit"
            [disabled]="isLoading"
            class="w-[180px]"
            [appDisableUnlessPermission]="newClient ? 'CLIENT_CREATE' : 'CLIENT_UPDATE'"
          >
            {{ newClient ? "Save New Client" : "Update Client" }}
          </button>
        </div> -->
          <div class="mt-5 space-x-5 flex justify-end">
            <!-- Cancel Button -->
            <button [routerLink]="['/admin/client']" appearance="outline" tuiButton type="button">
              Cancel
            </button>

            <!-- Save/Update Button -->
            <button appearance="primary" tuiButton type="submit" class="w-[200px]" [disabled]="isLoading"
              [appDisableUnlessPermission]="newClient ? 'CLIENT_CREATE' : 'CLIENT_UPDATE'">
              <!-- State: Not Loading -->
              <ng-container *ngIf="!isLoading">
                {{ newClient ? "Save New Client" : "Update Client" }}
              </ng-container>

              <!-- State: Loading -->
              <ng-container *ngIf="isLoading">
                {{ newClient ? "Saving..." : "Updating..." }}
              </ng-container>
            </button>
          </div>


        </div>
      </div>
    </form>
  </section>
</tui-loader>